// src/components/reports/VaccineReservationChart.js
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const VaccineReservationChart = ({ data, warehouseOptions }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
  };

  // กรองคลังที่มีข้อมูลใน data
  const availableWarehouses = [
    ...new Set(data.map((item) => item.WarehouseName)),
  ].filter(Boolean); // กรองค่าที่ไม่ใช่ null/undefined
  const filteredWarehouseOptions = warehouseOptions.filter(
    (warehouse) =>
      warehouse.WarehouseName &&
      availableWarehouses.includes(warehouse.WarehouseName)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (!selectedWarehouse) {
      // รวมยอดทุกคลังตาม VaccineNameTH
      const groupedData = data.reduce((acc, item) => {
        const vaccineName = item.VaccineNameTH;
        if (!acc[vaccineName]) {
          acc[vaccineName] = {
            name: vaccineName,
            farmerQuantity: 0,
            officeQuantity: 0,
            vaccineEN: item.VaccineNameEN,
            warehouse: "ทุกคลัง",
          };
        }
        acc[vaccineName].farmerQuantity += item.TotalQuantityFarmerReservation;
        acc[vaccineName].officeQuantity += item.TotalQuantityOfficeOrder;
        return acc;
      }, {});

      return Object.values(groupedData);
    } else {
      // กรองข้อมูลตามคลังที่เลือก
      return data
        .filter((item) => item.WarehouseName === selectedWarehouse)
        .map((item) => ({
          name: item.VaccineNameTH,
          farmerQuantity: item.TotalQuantityFarmerReservation,
          officeQuantity: item.TotalQuantityOfficeOrder,
          vaccineEN: item.VaccineNameEN,
          warehouse: item.WarehouseName,
        }));
    }
  };

  // กำหนดสีสำหรับกราฟ
  const farmerBarColor = "#16A085"; // Green, matching iGreen.500
  const officeBarColor = "#F39C12"; // Orange, for contrast

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ชื่อวัคซีน (TH):</strong> {label}
          </p>
          <p>
            <strong>ชื่อวัคซีน (EN):</strong> {data.vaccineEN}
          </p>
          <p>
            <strong>ชื่อคลัง:</strong> {data.warehouse}
          </p>
          <p>
            <strong>จำนวนจอง (เกษตรกร):</strong> {data.farmerQuantity}
          </p>
          <p>
            <strong>จำนวนจอง (หน่วยงาน):</strong> {data.officeQuantity}
          </p>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <StandardHeader
        title="กราฟยอดการสั่งจองวัคซีน"
        bgColor="iGreen.500"
        icon={faChartBar}
      />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกคลัง
        </FormLabel>
        {filteredWarehouseOptions.length > 0 ? (
          <Select
            value={selectedWarehouse}
            onChange={handleWarehouseChange}
            placeholder="ทุกคลัง"
            colorScheme="iBlue"
          >
            {filteredWarehouseOptions.map((warehouse) => (
              <option
                key={warehouse.WarehouseID}
                value={warehouse.WarehouseName}
              >
                {warehouse.WarehouseName}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีคลังที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวนจอง (หน่วย)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="farmerQuantity"
              name="ยอดจองเกษตรกร"
              fill={farmerBarColor}
              stackId="a"
              barSize={25}
            />
            <Bar
              dataKey="officeQuantity"
              name="ยอดจองหน่วยงาน"
              fill={officeBarColor}
              stackId="a"
              barSize={25}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineReservationChart;
